// Deterministic Fit-Score utility — no AI, no randomness, no async.
// Given an opportunity row + a user's founder profile, returns an integer
// 0–100 representing how well this opp matches the user's constraints.
//
// The formula is intentionally simple & explainable: 3 sub-scores (technical
// feasibility, budget fit, time fit) averaged with equal weight. Each
// sub-score lives in [0, 100]. Same opp + same profile → same fit forever.

const _clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Technical feasibility — how well does the opp's required complexity match
// the user's stack? Uses the opp's `automation_score` as a proxy for "how
// much of this can be glued together vs. coded from scratch".
const _scoreStack = (opp, stack) => {
  const auto = Number(opp.automation_score || 0); // 0–100
  switch (stack) {
    case "no_code":
      // No-code founders thrive on highly-automatable opps; punish low-automation
      return _clamp(20 + auto * 0.8, 0, 100);
    case "indie_hacker":
      // Sweet spot is mid-automation (some coding, lots of glue).
      // Bell-curve around 60: 100 - |auto-60|*0.9
      return _clamp(100 - Math.abs(auto - 60) * 0.9, 30, 100);
    case "full_stack":
      // Engineers can build anything — slight bonus for harder opps where
      // they have a moat over no-coders.
      return _clamp(60 + (100 - auto) * 0.4, 50, 100);
    case "team":
      // Teams can attack any opp; small bonus for complex builds where the
      // team's parallelism is the actual advantage.
      return _clamp(70 + (100 - auto) * 0.3, 60, 100);
    default:
      return 50;
  }
};

// Budget fit — does the opp's pricing line up with what the user can spend
// to bring it to market? We use the opp's `suggested_price` as a proxy for
// the size of the market the user would be selling into.
const _scoreBudget = (opp, budget) => {
  const price = Number(opp.suggested_price || 0); // monthly $
  switch (budget) {
    case "under_500":
      // Tight budget → favor cheap-to-build opps (lower priced ones tend to
      // be simpler to ship). Heavy penalty on big-ticket enterprise stuff.
      if (price <= 30) return 95;
      if (price <= 80) return 75;
      if (price <= 200) return 50;
      return 25;
    case "500_to_5k":
      // Most indie-hacker friendly band — covers cheap-to-mid pricing tiers.
      if (price <= 30) return 80;
      if (price <= 100) return 95;
      if (price <= 300) return 75;
      return 45;
    case "over_5k":
      // Bigger budget unlocks higher-ACV plays.
      if (price <= 50) return 60;
      if (price <= 200) return 85;
      return 95;
    default:
      return 50;
  }
};

// Time fit — does the user have enough hours to actually ship this?
// Uses the opp's `speed_score` (speed-to-MVP) as the proxy.
const _scoreHours = (opp, hours) => {
  const speed = Number(opp.speed_score || 0); // higher = faster to MVP
  switch (hours) {
    case "under_5":
      // Tight on time → only fast-to-MVP opps make sense.
      if (speed >= 80) return 95;
      if (speed >= 60) return 65;
      return 30;
    case "5_to_15":
      // Most indie-hacker band — can ship most things in a few weeks.
      if (speed >= 70) return 95;
      if (speed >= 50) return 80;
      return 55;
    case "over_15":
      // Plenty of time — even slow-to-MVP opps are realistic.
      if (speed >= 50) return 90;
      return 70;
    default:
      return 50;
  }
};

export const computeFitScore = (opp, profile) => {
  if (!opp || !profile || profile.skipped || !profile.stack) return null;
  const s = _scoreStack(opp, profile.stack);
  const b = _scoreBudget(opp, profile.budget);
  const h = _scoreHours(opp, profile.hours_per_week);
  return Math.round((s + b + h) / 3);
};

// Color/label helpers for the chip — pure presentation, deterministic.
export const fitTone = (score) => {
  if (score == null) return null;
  if (score >= 80) return { label: "Strong fit",  cls: "bg-emerald-100 text-emerald-700 ring-emerald-200" };
  if (score >= 60) return { label: "Good fit",    cls: "bg-blue-100 text-blue-700 ring-blue-200" };
  if (score >= 40) return { label: "Stretch fit", cls: "bg-amber-100 text-amber-700 ring-amber-200" };
  return                  { label: "Poor fit",    cls: "bg-rose-100 text-rose-700 ring-rose-200" };
};

// Human-readable labels for the chips on the onboarding modal & elsewhere.
export const PROFILE_LABELS = {
  stack: {
    no_code:      "No-code",
    indie_hacker: "Indie hacker",
    full_stack:   "Full-stack engineer",
    team:         "Team / multiple devs",
  },
  budget: {
    under_500:  "< $500",
    "500_to_5k": "$500 – $5k",
    over_5k:    "$5k+",
  },
  hours_per_week: {
    under_5:  "< 5 hours / week",
    "5_to_15": "5 – 15 hours / week",
    over_15:  "15+ hours / week",
  },
};
